import { type InitResult } from '@airwallex/components-sdk'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { type ReactNode, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'

import { useConfig } from '@/providers/config'
import { useConnectedAccount } from '@/providers/connectedAccount'
import { useUserContext } from '@/providers/user/useUserContext'
import { initializeSCASDK } from '@/utils/initializeSCASDK'
import { SCACancelledError } from '@/utils/scaErrors'
import { testAwareMs } from '@/utils/testAwareMs'

import { SCAContext } from './SCAContext'
import type { SCARetryHandler } from './types'

type SCAProviderProps = {
  children: ReactNode
}

export function SCAProvider({ children }: SCAProviderProps) {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()
  const { activeConnectedAccount } = useConnectedAccount()
  const { config } = useConfig()

  const vesselId = activeConnectedAccount?.vesselId

  const [isOpen, setIsOpen] = useState(false)
  const [scaSessionCode, setScaSessionCode] = useState<string | null>(null)
  const queuedHandlersRef = useRef<SCARetryHandler[]>([])
  const startingRef = useRef(false)
  const cancelGenerationRef = useRef(0)

  // SDK instance management
  const sdkInitResultRef = useRef<InitResult | null>(null)
  const initializingSdkRef = useRef(false)

  /**
   * Ensures the SDK is initialized and returns the SDK instance.
   * This is shared between token retrieval and the modal.
   */
  const ensureSDKInitialized = async (): Promise<InitResult | null> => {
    // Already initialized
    if (sdkInitResultRef.current) {
      return sdkInitResultRef.current
    }

    // Already initializing, wait for it
    if (initializingSdkRef.current) {
      // Wait for initialization to complete (simple polling, could be improved)
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!initializingSdkRef.current && sdkInitResultRef.current) {
            clearInterval(checkInterval)
            resolve(sdkInitResultRef.current)
          } else if (!initializingSdkRef.current) {
            clearInterval(checkInterval)
            resolve(null)
          }
        }, testAwareMs(100))
        setTimeout(() => {
          clearInterval(checkInterval)
          resolve(null)
        }, testAwareMs(5000))
      })
    }

    // Need to initialize
    if (!activeOrganizationId || !activeConnectedAccountId || !vesselId || !config?.awx.clientId) {
      return null
    }

    initializingSdkRef.current = true
    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        return null
      }

      const initResult = await initializeSCASDK({
        orgId: activeOrganizationId,
        vesselId,
        connectedAccountId: activeConnectedAccountId,
        accessToken,
        config: {
          clientId: config.awx.clientId,
          env: config.awx.env as 'dev' | 'staging' | 'demo' | 'prod',
        },
      })

      sdkInitResultRef.current = initResult
      return initResult
    } catch (error) {
      console.error('Failed to initialize SDK:', error)
      return null
    } finally {
      initializingSdkRef.current = false
    }
  }

  /**
   * Retrieves an SCA token using this flow:
   * 2. Try SDK getScaToken() (silent refresh if refresh token exists)
   * 3. Return null (triggers modal flow)
   *
   * @see https://www.airwallex.com/docs/banking-as-a-service__strong-customer-authentication-(sca)__sca-for-transaction-data-retrieval#step-2:-retrieve-the-sca-token-from-the-sdk
   */
  const getSCAToken = async (): Promise<string | null> => {
    // Step 1: Try SDK token retrieval (silent refresh)
    const sdk = await ensureSDKInitialized()
    try {
      const token = await sdk?.sca?.getScaToken()
      if (token) {
        return token
      }
    } catch (error) {
      // No refresh token available - this is normal, will show modal
      console.debug('SDK getScaToken failed (no refresh token):', error)
    }

    // Step 2: Return null to trigger modal flow
    return null
  }

  const getSCAFlowGeneration = () => cancelGenerationRef.current

  const resetSCAFlow = () => {
    sdkInitResultRef.current = null
    setIsOpen(false)
    setScaSessionCode(null)
    startingRef.current = false
  }

  // Single-flight guard so only one modal is opened per SCA flow
  const isFlowActive = useMemo(() => isOpen && !!scaSessionCode, [isOpen, scaSessionCode])

  const requestSCA = (sessionCode: string, handler: SCARetryHandler, flowGeneration: number) => {
    if (flowGeneration < cancelGenerationRef.current) {
      handler.onCancel(new SCACancelledError())
      return
    }

    queuedHandlersRef.current.push(handler)

    if (isFlowActive || startingRef.current) {
      return
    }

    // Force fresh SDK init for every new SCA flow (e.g. payment transfer)
    sdkInitResultRef.current = null
    initializingSdkRef.current = false

    startingRef.current = true
    setScaSessionCode(sessionCode)
    setIsOpen(true)
  }

  const handleSCASuccess = async (scaToken: string) => {
    try {
      const handlers = queuedHandlersRef.current
      queuedHandlersRef.current = []

      // Resolve all queued requests with the same token
      await Promise.allSettled(handlers.map((h) => h.onToken(scaToken)))
      toast.success('Authentication successful!')
    } catch (error) {
      console.error('Error retrying request after SCA:', error)
      toast.error('Failed to complete the operation. Please try again.')
    } finally {
      resetSCAFlow()
    }
  }

  const handleSCAError = (error: Error) => {
    console.error('SCA verification failed:', error)
    toast.error('Authentication failed. Please try again.')
    // Reject all queued requests
    const handlers = queuedHandlersRef.current
    queuedHandlersRef.current = []
    handlers.forEach((h) => h.onCancel(error))
    resetSCAFlow()
  }

  const handleSCACancel = () => {
    cancelGenerationRef.current += 1
    const handlers = queuedHandlersRef.current
    queuedHandlersRef.current = []
    handlers.forEach((handler) => handler.onCancel(new SCACancelledError()))
    if (handlers.length > 0) {
      toast.error('Authentication has been cancelled. Protected information is unavailable.', { id: 'sca-cancelled' })
    }
    resetSCAFlow()
  }

  const contextValue = {
    // Modal state
    isOpen,
    scaSessionCode,

    // Token management
    getSCAToken,

    // Modal control
    requestSCA,
    getSCAFlowGeneration,
    handleSCASuccess,
    handleSCAError,
    handleSCACancel,

    // SDK instance
    ensureSDKInitialized,
  }

  return <SCAContext.Provider value={contextValue}>{children}</SCAContext.Provider>
}
